import React, { useEffect, useState, useMemo } from 'react';

import { Button, Col, Form, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faCopy, faRedoAlt, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ToggleSwitch from 'react-switch';
import { useParams } from 'react-router-dom';

import { anchovy, filterContainerBoarder, grape } from 'lib/css/colors';
import Container from 'components/shared/Container';
import { handleCopyToClipboard } from 'lib/utils/stringUtils';
import { createGoogleSearchUrl } from 'lib/utils/webSearchUtils';
import ImageMagnifier from 'components/shared/ImageMagnifier';
import ImageUploadModal from 'components/shared/ImageUploadModal';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import SuggestedThumbnailsView from 'components/product_lists/product_list/SuggestedThumbnailsView';
import {
  TEMPORARY_PRODUCT_IMAGE_GET_PRODUCTS,
  TEMPORARY_PRODUCT_IMAGE_UPDATE_TEMP_IMAGE,
} from 'lib/networking/endpoints';
import { SwalToast } from 'lib/utils/toast';
import {
  TemporaryProductImageEnums,
  TemporaryProductImagesDisplayValues,
} from 'lib/enums/TemporaryProductImageEnums';
import UserPermission from 'lib/enums/UserPermission';
import useGet from 'lib/hooks/useGet';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import usePost from 'lib/hooks/usePost';
import useUserPermissions from 'lib/hooks/useUserPermissions';

function AssignThumbnailsView() {
  const hasUserPermissions = useUserPermissions();

  const reviewAssignedThumbnails = useMemo(
    () => hasUserPermissions([UserPermission.REVIEW_ASSIGNED_THUMBNAILS]),
    [hasUserPermissions],
  );

  const { ProductListId, ProductListName } = useParams();
  const [selectedThumbnailUrl, setSelectedThumbnailUrl] = useState('');
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [aiSuggestedOnly, setAiSuggestedOnly] = useLocalStorage('aiSuggestedOnly', false);
  const [traineeApprovedOnly, setTraineeApprovedOnly] = useLocalStorage(
    'traineeApprovedOnly',
    false,
  );

  const {
    data: { product, temp_image } = {},
    loading: loading,
    refetch: refetch,
  } = useGet(TEMPORARY_PRODUCT_IMAGE_GET_PRODUCTS, {
    product_list_id: ProductListId,
    ai_suggested_only: aiSuggestedOnly,
    trainee_approved_only: traineeApprovedOnly,
  });

  const {
    data: { missing_count, total_count, count_for_user } = {},
    loading: loadingCount,
    refetch: refetchCount,
  } = useGet(TEMPORARY_PRODUCT_IMAGE_GET_PRODUCTS, {
    product_list_id: ProductListId,
    count_only: true,
    ai_suggested_only: aiSuggestedOnly,
    trainee_approved_only: traineeApprovedOnly,
  });

  useEffect(() => {
    setSelectedThumbnailUrl(temp_image?.image_url || product?.global_product?.default_image_url);
    refetchCount();
  }, [product, temp_image, refetchCount]);

  const { postData: vote } = usePost(
    TEMPORARY_PRODUCT_IMAGE_UPDATE_TEMP_IMAGE,
    () => {
      SwalToast('success', `Marked`);
      refetch();
      setSelectedThumbnailUrl('');
      setUploadedImages([]);
    },
    () => SwalToast('error', `Could Not add the vote`),
  );

  return (
    <Container>
      <div className="d-flex justify-content-start align-items-end">
        <h2 className="ml-2 mb-2">{ProductListName}</h2>
        {reviewAssignedThumbnails && (
          <>
            <Form.Group className="d-flex flex-column align-items-start ml-3">
              <Form.Label>AI suggested only</Form.Label>
              <ToggleSwitch
                onColor={grape}
                checked={aiSuggestedOnly}
                checkedIcon={false}
                uncheckedIcon={false}
                onChange={() => {
                  const newValue = !aiSuggestedOnly;
                  setAiSuggestedOnly(newValue);
                  if (newValue) setTraineeApprovedOnly(false);
                }}
              />
            </Form.Group>
            <Form.Group className="d-flex flex-column align-items-start ml-3">
              <Form.Label>Trainee approved only</Form.Label>
              <ToggleSwitch
                className="ml-2"
                onColor={grape}
                checked={traineeApprovedOnly}
                checkedIcon={false}
                uncheckedIcon={false}
                onChange={() => {
                  const newValue = !traineeApprovedOnly;
                  setTraineeApprovedOnly(newValue);
                  if (newValue) setAiSuggestedOnly(false);
                }}
              />
            </Form.Group>
          </>
        )}
      </div>
      {loading ? (
        <div className={css(styles.spinnerOverlay)}>
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {!temp_image ? (
            reviewAssignedThumbnails ? (
              <div>No populated products to display for the product list</div>
            ) : (
              <div>No products left to assign thumbnails in the product list</div>
            )
          ) : (
            <>
              <Col className={classNames(css(styles.filterContainer), 'my-2 p-3')}>
                <Row>
                  <Col md={2}>
                    <div>
                      <ImageMagnifier
                        src={selectedThumbnailUrl || placeholderProductImage}
                        imgClassName={classNames(css(styles.square), pointerOnHover)}
                        magnifierSize={800}
                        zoom={6}
                        offsetX={30}
                        offsetY={-50}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div>
                      <strong className={css(styles.productName)}>
                        {temp_image.global_product_vendor?.name}
                      </strong>
                      <OverlayTrigger overlay={<Tooltip>Search Images on Google</Tooltip>}>
                        <FontAwesomeIcon
                          icon={faSearch}
                          onClick={() =>
                            window.open(
                              createGoogleSearchUrl(
                                [
                                  temp_image.global_product_vendor?.name,
                                  temp_image.global_product_vendor?.brand?.name,
                                  temp_image.global_product_vendor?.manufacturer?.name,
                                  temp_image.global_product_vendor?.pack_size,
                                ],
                                true,
                              ),
                              '_blank',
                            )
                          }
                          className={pointerOnHover}
                        />
                      </OverlayTrigger>
                      <OverlayTrigger overlay={<Tooltip>Copy search query to clipboard</Tooltip>}>
                        <FontAwesomeIcon
                          icon={faCopy}
                          className={classNames(pointerOnHover, 'ml-2')}
                          onClick={() =>
                            handleCopyToClipboard(
                              createGoogleSearchUrl(
                                [
                                  temp_image.global_product_vendor?.name,
                                  temp_image.global_product_vendor?.brand?.name,
                                  temp_image.global_product_vendor?.manufacturer?.name,
                                  temp_image.global_product_vendor?.pack_size,
                                ],
                                false,
                              ),
                            )
                          }
                        />
                      </OverlayTrigger>
                      <Button
                        variant="link"
                        onClick={() =>
                          window.open(
                            createGoogleSearchUrl(
                              [
                                temp_image.global_product_vendor?.name,
                                temp_image.global_product_vendor?.brand?.name,
                                temp_image.global_product_vendor?.manufacturer?.name,
                                temp_image.global_product_vendor?.pack_size,
                              ],
                              true,
                            ),
                            '_blank',
                          )
                        }
                        className={pointerOnHover}
                      >
                        Search on Google
                      </Button>
                      <div>
                        <OverlayTrigger overlay={<Tooltip>Copy brand to clipboard</Tooltip>}>
                          <FontAwesomeIcon
                            icon={faCopy}
                            className={classNames(pointerOnHover, 'ml-2')}
                            onClick={() =>
                              handleCopyToClipboard(temp_image.global_product_vendor?.brand?.name)
                            }
                          />
                        </OverlayTrigger>
                        <strong className="ml-2">Brand : </strong>
                        <span className="text-muted">
                          {temp_image.global_product_vendor?.brand?.name || '-'}
                        </span>
                      </div>
                      <div>
                        <OverlayTrigger overlay={<Tooltip>Copy manufacturer to clipboard</Tooltip>}>
                          <FontAwesomeIcon
                            icon={faCopy}
                            className={classNames(pointerOnHover, 'ml-2')}
                            onClick={() =>
                              handleCopyToClipboard(
                                temp_image.global_product_vendor?.manufacturer?.name,
                              )
                            }
                          />
                        </OverlayTrigger>
                        <strong className="ml-2">Manufacturer : </strong>
                        <span className="text-muted">
                          {temp_image.global_product_vendor?.manufacturer?.name || '-'}
                        </span>
                      </div>
                      <div>
                        <OverlayTrigger overlay={<Tooltip>Copy pack size to clipboard</Tooltip>}>
                          <FontAwesomeIcon
                            icon={faCopy}
                            className={classNames(pointerOnHover, 'ml-2')}
                            onClick={() =>
                              handleCopyToClipboard(temp_image.global_product_vendor?.pack_size)
                            }
                          />
                        </OverlayTrigger>
                        <strong className="ml-2">Pack size : </strong>
                        <span className="text-muted">
                          {temp_image.global_product_vendor?.pack_size || '-'}
                        </span>
                      </div>
                      <div>
                        <OverlayTrigger overlay={<Tooltip>Copy SKU to clipboard</Tooltip>}>
                          <FontAwesomeIcon
                            icon={faCopy}
                            className={classNames(pointerOnHover, 'ml-2')}
                            onClick={() =>
                              handleCopyToClipboard(temp_image.global_product_vendor?.vendor_sku)
                            }
                          />
                        </OverlayTrigger>
                        <strong className="ml-2">SKU: </strong>
                        <Button
                          variant="link"
                          onClick={() => {
                            const productId = temp_image.global_product_vendor?.global_product.id;
                            const vendorId = temp_image.global_product_vendor?.id;
                            const url =
                              `/global-product-detail/${productId}/` + `${vendorId}/distributor`;
                            window.open(url, '_blank');
                          }}
                        >
                          {temp_image.global_product_vendor?.vendor_sku}
                        </Button>
                      </div>
                      <div>
                        <OverlayTrigger overlay={<Tooltip>Copy GTIN to clipboard</Tooltip>}>
                          <FontAwesomeIcon
                            icon={faCopy}
                            className={classNames(pointerOnHover, 'ml-2')}
                            onClick={() =>
                              handleCopyToClipboard(temp_image.global_product_vendor?.gtin)
                            }
                          />
                        </OverlayTrigger>
                        <strong className="ml-2">GTIN : </strong>
                        <span className="text-muted">
                          {temp_image.global_product_vendor?.gtin || '-'}
                        </span>
                      </div>
                    </div>
                    {reviewAssignedThumbnails && (
                      <>
                        <div>
                          <strong>Previous vote : </strong>
                          <span className="text-muted">
                            {TemporaryProductImagesDisplayValues[temp_image?.op_vote]}
                          </span>
                        </div>
                        {(temp_image?.op_vote === TemporaryProductImageEnums.QA_TRAINEE_APPROVED ||
                          temp_image?.op_vote ===
                            TemporaryProductImageEnums.QA_TRAINEE_NOT_APPLICABLE) && (
                          <div>
                            <strong>Approved by : </strong>
                            <span className="text-muted">{temp_image?.op?.name}</span>
                          </div>
                        )}
                      </>
                    )}
                    <Row>
                      <Button
                        className={css(styles.button)}
                        variant="outline-success"
                        onClick={() => {
                          vote({
                            global_product_vendor_id: temp_image.global_product_vendor.id,
                            image_url: selectedThumbnailUrl,
                            vote: 'APPROVED',
                            temporary_product_image_id: temp_image?.id,
                          });
                        }}
                        disabled={!selectedThumbnailUrl}
                      >
                        Yes
                      </Button>
                      <Button
                        className={css(styles.button)}
                        variant="outline-danger"
                        onClick={() => setShowImageUploadModal(true)}
                      >
                        No
                      </Button>
                      <Button
                        className={css(styles.button)}
                        variant="outline-secondary"
                        onClick={() => {
                          vote({
                            global_product_vendor_id: temp_image.global_product_vendor.id,
                            image_url: null,
                            vote: 'NOT_APPLICABLE',
                            temporary_product_image_id: temp_image?.id,
                          });
                        }}
                        style={{ width: '170px' }}
                      >
                        Not Applicable
                      </Button>
                      {hasUserPermissions([UserPermission.ASSIGN_THUMBNAILS]) && (
                        <Button
                          className={css(styles.button)}
                          variant="outline-primary"
                          onClick={() => {
                            vote({
                              global_product_vendor_id: temp_image.global_product_vendor.id,
                              image_url: null,
                              vote: 'NOT_SURE',
                              temporary_product_image_id: temp_image?.id,
                            });
                          }}
                        >
                          Not Sure
                        </Button>
                      )}
                    </Row>
                  </Col>
                  <Col md={4} className="text-muted">
                    <Row className="justify-content-end mr-2">
                      {hasUserPermissions([UserPermission.REVIEW_ASSIGNED_THUMBNAILS]) && (
                        <OverlayTrigger overlay={<Tooltip>Reset populated images</Tooltip>}>
                          <FontAwesomeIcon
                            className={pointerOnHover}
                            icon={faRedoAlt}
                            spin={loading}
                            onClick={() =>
                              setSelectedThumbnailUrl(
                                temp_image?.image_url || product?.global_product?.default_image_url,
                              )
                            }
                          />
                        </OverlayTrigger>
                      )}
                      {loadingCount ? (
                        <Spinner animation="border" role="status" size="sm" />
                      ) : (
                        <>
                          <OverlayTrigger
                            overlay={<Tooltip>Thumbnail missing products in the catalogue</Tooltip>}
                          >
                            <div className="ml-2">
                              {missing_count}/{total_count} |
                            </div>
                          </OverlayTrigger>
                          <OverlayTrigger
                            overlay={<Tooltip>Products to go through in the catalogue</Tooltip>}
                          >
                            <div className="ml-2">
                              {count_for_user}/{total_count}
                            </div>
                          </OverlayTrigger>
                        </>
                      )}
                    </Row>
                  </Col>
                  {showImageUploadModal && (
                    <ImageUploadModal
                      header="Please upload a suitable thumbnail"
                      onHide={() => setShowImageUploadModal(false)}
                      onSubmit={postBody => {
                        vote(postBody);
                        setShowImageUploadModal(false);
                      }}
                      postBody={{
                        global_product_vendor_id: temp_image.global_product_vendor.id,
                        vote: 'APPROVED',
                        temporary_product_image_id: temp_image?.id,
                      }}
                      imageUrls={uploadedImages}
                      setImageUrls={setUploadedImages}
                      imageUrl={selectedThumbnailUrl}
                      setImageUrl={setSelectedThumbnailUrl}
                    />
                  )}
                </Row>
              </Col>
              <Row>
                <SuggestedThumbnailsView
                  product={temp_image.global_product_vendor}
                  temporaryProductImage={temp_image}
                  setSelectedThumbnailUrl={setSelectedThumbnailUrl}
                  uploadedImages={uploadedImages}
                  setUploadedImages={setUploadedImages}
                />
              </Row>
            </>
          )}
        </>
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  square: {
    width: '200px',
    height: '200px',
    objectFit: 'contain',
    border: '2px solid ' + anchovy,
  },
  filterContainer: {
    border: `3px solid ${filterContainerBoarder}`,
    width: 'auto',
    height: 'auto',
  },
  spinnerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: 1,
  },
  productName: {
    fontSize: 30,
    marginRight: '10px',
  },
  button: {
    width: '100px',
    maxHeight: '30px',
    marginLeft: '4px',
    marginRight: '4px',
  },
});

export default AssignThumbnailsView;
